/**
 * `LocalRepository` — the offline `DatasetRepository` (plan §1.2 · §3.3).
 *
 * A COMPOSER, not an implementation: every payload is built by one of the six
 * modules under `local/`, deliberately the same six-way partition as
 * `apps/api/src/services/`, so local-vs-live parity reads six-against-six rather
 * than one-against-six. When a live route drifts, the file that has to change is
 * the file with the same name.
 *
 * This module owns exactly ONE `createSeriesCache()` instance and injects it.
 * That is the only mutable structure in `core` (ADR-0004), and confining it to a
 * factory-scoped instance is what makes two independently-constructed
 * repositories provably independent — asserted by the "fresh instance identical
 * results" conformance test.
 *
 * Every method is `async` even though nothing here awaits, and `require-await`
 * is disabled below rather than worked around. Two reasons, both load-bearing:
 *
 *  · Plan §1.1 rule 1 — a synchronous local adapter would let a component detect
 *    which adapter is live, and it would be right.
 *  · `async` is what routes a SYNCHRONOUS throw into the returned promise.
 *    `resolveSecurity` throws `DatasetNotFoundError` eagerly, so the obvious
 *    `require-await`-clean rewrite — `() => Promise.resolve(build(...))` — would
 *    throw out of the CALL rather than reject the promise. Callers written
 *    against the port would then need a try/catch *and* a `.catch()`, and
 *    `HttpRepository`, whose failures are inherently async, would behave
 *    differently from this one on the single path where behaviour matters most.
 *    The port-conformance suite asserts exactly that (`does not throw
 *    synchronously`), so this is a tested guarantee, not a stylistic preference.
 */
import type {
  AreaDetail,
  AreaId,
  AreaListPayload,
  AreaQuery,
  BenchmarkOhlcQuery,
  BenchmarkOhlcResult,
  BookPayload,
  DashboardPayload,
  DashboardQuery,
  DatasetMeta,
  DiversificationPayload,
  MarketSnapshotPayload,
  OhlcSeries,
  PeersPayload,
  PriceSeries,
  SearchQuery,
  SectorDetail,
  SectorId,
  SectorQuery,
  Security,
  SecurityProfile,
  SeriesQuery,
  Ticker,
} from '@meridian/contracts';

import { bookOwnership } from '../selectors/filter-book';
import { createSeriesCache } from '../series/series-cache';
import { UNIVERSE } from '../universe/build-universe';

import { assignIndustries, buildAreaDetail, buildAreaList } from './local/area';
import { buildBenchmarkOhlc } from './local/benchmark-ohlc';
import { buildBenchmarkSeries } from './local/benchmark-series';
import { buildSyntheticBook } from './local/book';
import { buildDashboard } from './local/dashboard';
import {
  createLiveShapedRepository,
  SYNTHETIC_PROFILES,
  type SyntheticProfile,
} from './local/live-shaped';
import { buildMarketSnapshot } from './local/market-snapshot';
import { buildMeta } from './local/meta';
import { buildOhlcSeries } from './local/ohlc-series';
import { runSearch } from './local/search';
import { buildSectorDetail } from './local/sector';
import { buildDiversifiers, buildPeers, buildProfile, indexByTicker } from './local/security';
import { buildSeries, resolveSecurity } from './local/series';

import type { DatasetRepository } from '../ports/dataset-repository';
import type { DerivedSecurity } from '../universe/derive-security';

export interface LocalRepositoryOptions {
  /**
   * Row set to serve. Defaults to the frozen synthetic `UNIVERSE`.
   * Injectable so tests and fixtures can drive the adapter with a small set
   * without reaching into module state — and so nothing else in the package has
   * to import `build-universe`, which the dependency-cruiser rules restrict.
   */
  readonly rows?: readonly DerivedSecurity[];

  /**
   * Field-shape profile. Defaults to `demo-fidelity` — the reference dataset,
   * unchanged, which the golden digest and every existing fixture depend on.
   * `live-shaped` decorates the SAME payloads with production field shape
   * (plan §1.2 · §4.7); see `local/live-shaped.ts` for the seam and its price.
   */
  readonly profile?: SyntheticProfile;
}

/* eslint-disable @typescript-eslint/require-await -- see the header: `async` is
   the error-channelling mechanism here, not an idle keyword. */
export function createLocalRepository(options: LocalRepositoryOptions = {}): DatasetRepository {
  const rows = options.rows ?? UNIVERSE;
  const profile = options.profile ?? 'demo-fidelity';
  /* The value crosses a Zod-validated env boundary in `apps/web`, but nothing
     stops another caller passing a widened string, and an unrecognised profile
     silently serving demo data is precisely the outcome this option exists to
     prevent. */
  if (!SYNTHETIC_PROFILES.includes(profile)) {
    throw new Error(`createLocalRepository: unknown profile ${JSON.stringify(profile)}`);
  }
  /* An empty row set is not a small fixture, it is an unrepresentable dataset:
     every fan-out route would resolve an empty payload the wire schemas reject.
     Failing here names the cause; failing at serialisation names a field the
     caller never set. NOT justified by universeSize any more — ADR-0023 widened
     DatasetMetaSchema.universeSize to nonnegative so the LIVE first-run state
     (asOf null, no active securities) is a renderable payload. The synthetic
     path has no such state, which is why this guard stays. */
  if (rows.length === 0) {
    throw new Error('createLocalRepository: rows must not be empty');
  }
  const cache = createSeriesCache();
  const byTicker = indexByTicker(rows);
  /* Industry assignment is a pure function of the frozen row set, so it is
     computed once here beside `byTicker` rather than per request. Three read
     paths need it (`getAreas`, `getArea`, `getPeers`); recomputing it walked
     the universe once per sector on every one of them. */
  const assigned = assignIndustries(rows);
  /* Built once beside `assigned`, for the same reason: it is a pure function of
     the frozen row set, and three read paths narrow by it. Rebuilding it per
     request would walk the universe on every dashboard and sector read. */
  const book = buildSyntheticBook(rows);

  const demoFidelity: DatasetRepository = {
    getMeta: async (): Promise<DatasetMeta> => buildMeta(rows),

    getBook: async (): Promise<BookPayload> => book,

    getDashboard: async (query: DashboardQuery): Promise<DashboardPayload> =>
      buildDashboard(rows, query, bookOwnership(query.book, book.lots)),

    getMarketSnapshot: async (): Promise<MarketSnapshotPayload> => buildMarketSnapshot(),

    getSector: async (id: SectorId, query: SectorQuery): Promise<SectorDetail> =>
      buildSectorDetail(rows, id, query, bookOwnership(query.book, book.lots)),

    getAreas: async (query: AreaQuery): Promise<AreaListPayload> =>
      buildAreaList(rows, query, { assigned, owned: bookOwnership(query.book, book.lots) }),

    getArea: async (id: AreaId, query: AreaQuery): Promise<AreaDetail> =>
      buildAreaDetail(rows, id, query, { assigned, owned: bookOwnership(query.book, book.lots) }),

    /* `_query` is accepted for port symmetry. Synthetic vol/beta are scalars,
       not a window join — live mode is where the window changes the numbers. */
    getSecurity: async (ticker: Ticker, _query: SeriesQuery): Promise<SecurityProfile> =>
      buildProfile(resolveSecurity(byTicker, ticker), cache),

    getSeries: async (ticker: Ticker, query: SeriesQuery): Promise<PriceSeries> =>
      buildSeries(resolveSecurity(byTicker, ticker), cache, query),

    getBenchmarkSeries: async (query: SeriesQuery): Promise<PriceSeries> =>
      buildBenchmarkSeries(rows, query),

    getOhlcSeries: async (ticker: Ticker, query: SeriesQuery): Promise<OhlcSeries> =>
      buildOhlcSeries(resolveSecurity(byTicker, ticker), cache, query),

    getBenchmarkOhlcSeries: async (query: BenchmarkOhlcQuery): Promise<BenchmarkOhlcResult> =>
      buildBenchmarkOhlc(rows, query),

    getPeers: async (ticker: Ticker, query: SeriesQuery): Promise<PeersPayload> =>
      buildPeers(rows, resolveSecurity(byTicker, ticker), query, assigned),

    getDiversifiers: async (ticker: Ticker, query: SeriesQuery): Promise<DiversificationPayload> =>
      buildDiversifiers(rows, resolveSecurity(byTicker, ticker), query),

    search: async (query: SearchQuery): Promise<readonly Security[]> => runSearch(rows, query),
  };

  /* `demo-fidelity` returns the UNDECORATED repository — the default profile is
     unchanged by construction, not merely by test. */
  return profile === 'live-shaped' ? createLiveShapedRepository(demoFidelity, rows) : demoFidelity;
}
/* eslint-enable @typescript-eslint/require-await */
